import { useState, useEffect } from 'react';
import { Radio, Wifi, WifiOff, AlertTriangle, CreditCard, Activity, Building2 } from 'lucide-react';
import { api } from '../../api/client';

interface RfidDashboardData {
  dispositivos: {
    total: number;
    online: number;
    offline: number;
    falha: number;
    taxaSaude: number;
  };
  credenciais: {
    total: number;
    ativas: number;
    inativas: number;
  };
  leiturasHoje: number;
  escolasComRfid: number;
}

export function MasterRfidDashboard() {
  const [data, setData] = useState<RfidDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/master/dashboard/rfid')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Carregando dashboard RFID...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 32, color: 'var(--text-muted)' }}>Erro ao carregar dados RFID.</div>
    );
  }

  const cards = [
    {
      label: 'Dispositivos',
      value: data.dispositivos.total,
      icon: Radio,
      color: '#6366f1',
      bg: '#eef2ff',
    },
    {
      label: 'Online',
      value: data.dispositivos.online,
      icon: Wifi,
      color: '#10b981',
      bg: '#ecfdf5',
    },
    {
      label: 'Offline',
      value: data.dispositivos.offline,
      icon: WifiOff,
      color: '#ef4444',
      bg: '#fef2f2',
    },
    {
      label: 'Falha/Manutencao',
      value: data.dispositivos.falha,
      icon: AlertTriangle,
      color: '#f59e0b',
      bg: '#fffbeb',
    },
    {
      label: 'Credenciais Ativas',
      value: data.credenciais.ativas,
      icon: CreditCard,
      color: '#8b5cf6',
      bg: '#f5f3ff',
    },
    {
      label: 'Credenciais Inativas',
      value: data.credenciais.inativas,
      icon: CreditCard,
      color: '#94a3b8',
      bg: '#f8fafc',
    },
    {
      label: 'Leituras Hoje',
      value: data.leiturasHoje,
      icon: Activity,
      color: '#0ea5e9',
      bg: '#f0f9ff',
    },
    {
      label: 'Escolas com RFID',
      value: data.escolasComRfid,
      icon: Building2,
      color: '#14b8a6',
      bg: '#f0fdfa',
    },
  ];

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Radio size={24} style={{ color: '#6366f1' }} />
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>
            Dashboard RFID
          </h1>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>
            Visao consolidada de todos os dispositivos e credenciais
          </p>
        </div>
      </div>

      {/* Saude geral */}
      <div style={{
        background: data.dispositivos.taxaSaude >= 90 ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' :
          data.dispositivos.taxaSaude >= 70 ? 'linear-gradient(135deg, #fffbeb, #fef3c7)' :
          'linear-gradient(135deg, #fef2f2, #fecaca)',
        borderRadius: 12,
        padding: '18px 24px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
            Saude da Infraestrutura RFID
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: data.dispositivos.taxaSaude >= 90 ? '#059669' : data.dispositivos.taxaSaude >= 70 ? '#d97706' : '#dc2626' }}>
            {data.dispositivos.taxaSaude}%
          </div>
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'right' as const }}>
          <div>{data.dispositivos.online} de {data.dispositivos.total} dispositivos online</div>
          <div>{data.credenciais.ativas} credenciais ativas</div>
        </div>
      </div>

      {/* Cards grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 16,
      }}>
        {cards.map((card) => (
          <div
            key={card.label}
            style={{
              background: 'var(--bg-card, #fff)',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: 12,
              padding: '18px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              transition: 'box-shadow 0.2s',
            }}
          >
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: card.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <card.icon size={20} style={{ color: card.color }} />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>
                {card.value.toLocaleString('pt-BR')}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                {card.label}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
